import { useEstadoNaUrl } from "@/lib/estado-na-url";
import { origemAtual } from "@/lib/navegacao";
import { useQuery } from "@tanstack/react-query";
import { Link } from "wouter";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ArrowLeft, MoreHorizontal, Download, Filter } from "lucide-react";
import { useMemo, useState } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import * as XLSX from "xlsx";
import type { Ciclo, Mangara, Colheita, Perda, CorFita, Variedade, SetorWithVariedade } from "@shared/schema";
import { useSetores } from "@/hooks/use-setores";
import { useVariedades } from "@/hooks/use-variedades-cacho";

import { PageHeader, ALVO_TOQUE_BOTAO } from "@/components/layout/page-header";
import { DataCard } from "@/components/data/data-card";
import {
  DataTable, DataTableHeader, DataTableBody, DataTableRow,
  DataTableHead, DataTableCell, DataTableNum, DataTableFooter,
} from "@/components/data/data-table";
import { StatusBadge } from "@/components/data/status-badge";
import { EmptyState } from "@/components/data/empty-state";
import { PageSkeleton } from "@/components/data/page-skeleton";
type CicloWithRelations = Ciclo & {
  corFita?: CorFita;
  dataPrevisaoInicio?: Date | null;
  dataPrevisaoFim?: Date | null;
};

type FiltroPeriodo = "tudo" | "ano_atual" | "ano_anterior" | "mes_atual" | "mes_anterior" | "personalizado";

const COLUNAS = ["9rem", "10rem", "8rem", "7rem", "7rem", "8rem", "8rem", "7rem", "7rem", "8rem", "8rem", "9rem", "3.5rem"];

// ⚠️ Constantes de MÓDULO (deps de useMemo dentro do hook).
const PADRAO_PERFORMANCE = {
  setor: "todos",
  cor: "todas",
  status: "todos",
  periodo: "tudo",
  de: "",
  ate: "",
};
const PERMITIDOS_PERFORMANCE = {
  status: ["todos", "EM_ANDAMENTO", "PROXIMO", "FINALIZAR_CICLO", "FINALIZADO"],
  periodo: ["tudo", "ano_atual", "ano_anterior", "mes_atual", "mes_anterior", "personalizado"],
} as const;

export default function RelatoriosPerformanceSetorPage() {
  const { toast } = useToast();
  const { estado: filtros, definir } = useEstadoNaUrl(PADRAO_PERFORMANCE, PERMITIDOS_PERFORMANCE);
  const setorFilter = filtros.setor;
  const corFitaFilter = filtros.cor;
  const statusFilter = filtros.status;
  const filtroPeriodo = filtros.periodo as FiltroPeriodo;
  const dataInicio = filtros.de;
  const dataFim = filtros.ate;

  const { data: ciclos, isLoading: loadingCiclos } = useQuery<CicloWithRelations[]>({
    queryKey: ["/api/ciclos"],
  });

  const { setores, isLoading: loadingSetores } = useSetores({ somenteProdutivos: true });

  const { data: mangaras, isLoading: loadingMangaras } = useQuery<Mangara[]>({
    queryKey: ["/api/mangaras"],
  });

  const { data: colheitas } = useQuery<Colheita[]>({
    queryKey: ["/api/colheitas"],
  });

  const { data: perdas } = useQuery<Perda[]>({
    queryKey: ["/api/perdas"],
  });

  const { data: cores } = useQuery<CorFita[]>({
    queryKey: ["/api/cores"],
  });

  // Lista COMPLETA: aqui variedade só é LOOKUP (o nome da variedade do setor),
  // não há seletor de variedade nesta tela — não há nada a esconder, e filtrar
  // deixaria um setor de outra cultura sem rótulo.
  const { data: variedades } = useVariedades();

  const isLoading = loadingCiclos || loadingSetores || loadingMangaras;

  // Calculate performance data per setor+ciclo
  const performanceData = useMemo(() => {
    if (!ciclos || !setores || !mangaras) return [];

    const data: {
      setor: SetorWithVariedade;
      ciclo: CicloWithRelations;
      cor: CorFita | undefined;
      cicloCode: string;
      semanasPrevistas: number;
      semanasRealizadas: number | null;
      planejado: number;
      colhido: number;
      perda: number;
      saldo: number;
      percentualColhido: number;
      percentualPerda: number;
      status: 'FINALIZADO' | 'FINALIZAR_CICLO' | 'EM_ANDAMENTO' | 'PROXIMO';
      dataPrevisaoInicio: Date | null;
      dataPrevisaoFim: Date | null;
      anoPrevisto: number;
      semanaPrevista: number;
    }[] = [];

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Group by setor + ciclo
    setores.forEach(setor => {
      const setorMangaras = mangaras.filter(m => m.setorId === setor.id);
      const cicloIds = Array.from(new Set(setorMangaras.map(m => m.cicloId)));

      cicloIds.forEach(cicloId => {
        const ciclo = ciclos.find(c => c.id === cicloId);
        if (!ciclo) return;

        const cor = cores?.find(c => c.id === ciclo.corFitaId);
        const mangarasCicloSetor = setorMangaras.filter(m => m.cicloId === cicloId);
        const colheitasCicloSetor = colheitas?.filter(c => c.cicloId === cicloId && c.setorId === setor.id) || [];
        const perdasCicloSetor = perdas?.filter(p => p.cicloId === cicloId && p.setorId === setor.id) || [];

        const planejado = mangarasCicloSetor.reduce((sum, m) => sum + (m.quantidadeCachos || 0), 0);
        const colhido = colheitasCicloSetor.reduce((sum, c) => sum + (c.quantidadeCachos || 0), 0);
        const perda = perdasCicloSetor.reduce((sum, p) => sum + (p.quantidadeCachos || 0), 0);
        const saldo = planejado - colhido - perda;
        const percentualColhido = planejado > 0 ? (colhido / planejado) * 100 : 0;
        const percentualPerda = planejado > 0 ? (perda / planejado) * 100 : 0;

        // Get first mangara data
        const firstMangara = mangarasCicloSetor[0];
        const semanaPrevista = firstMangara?.semanaPrevistaColheita || 0;
        const anoPrevisto = firstMangara?.anoSemanaPrevistaColheita || new Date().getFullYear();

        // Get semanas previstas (maturation weeks from variety)
        const variedade = variedades?.find(v => v.id === setor.variedadeId);
        const mesAnoStr = ciclo.mesAno || '';
        const meses: Record<string, number> = {
          jan: 1, fev: 2, mar: 3, abr: 4, mai: 5, jun: 6,
          jul: 7, ago: 8, set: 9, out: 10, nov: 11, dez: 12
        };
        const mesNum = meses[mesAnoStr.substring(0, 3).toLowerCase()] || 0;
        const semanasPrevistas = variedade?.semanasMaturacaoPorMes?.[mesNum.toString()] || 0;

        // Calculate realized weeks using weighted average (days × quantity / total quantity)
        let semanasRealizadas: number | null = null;
        if (colheitasCicloSetor.length > 0 && mangarasCicloSetor.length > 0) {
          let somaPonderada = 0;
          let somaPesos = 0;

          for (const colheita of colheitasCicloSetor) {
            const dataColheita = new Date(colheita.dataColheita);
            dataColheita.setHours(0, 0, 0, 0);
            const quantidade = colheita.quantidadeCachos || 0;

            // Find corresponding mangará (same sector, ribbon color, cycle) with cut date <= harvest date
            const mangarasValidos = mangarasCicloSetor.filter(m => {
              if (m.corFitaId !== colheita.corFitaId) return false;
              const dataCorte = new Date(m.dataCorteMangara);
              dataCorte.setHours(0, 0, 0, 0);
              return dataCorte <= dataColheita;
            });

            if (mangarasValidos.length > 0) {
              // Get most recent mangará before harvest
              const mangaraMaisRecente = mangarasValidos.reduce((latest, current) => {
                return new Date(current.dataCorteMangara) > new Date(latest.dataCorteMangara) ? current : latest;
              });

              const dataCorte = new Date(mangaraMaisRecente.dataCorteMangara);
              dataCorte.setHours(0, 0, 0, 0);
              const diasDiff = Math.floor((dataColheita.getTime() - dataCorte.getTime()) / (1000 * 60 * 60 * 24));

              if (quantidade > 0) {
                somaPonderada += diasDiff * quantidade;
                somaPesos += quantidade;
              }
            }
          }

          if (somaPesos > 0) {
            const diasMediaPonderada = somaPonderada / somaPesos;
            semanasRealizadas = Math.round((diasMediaPonderada / 7) * 10) / 10; // 1 decimal
          }
        }

        // Determine status - matching logic from ciclos.tsx
        let status: 'FINALIZADO' | 'FINALIZAR_CICLO' | 'EM_ANDAMENTO' | 'PROXIMO' = 'PROXIMO';
        
        if (ciclo.status === 'FECHADO_MANUAL' || ciclo.status === 'FECHADO') {
          status = 'FINALIZADO';
        } else {
          // Parse dates the same way as ciclos.tsx
          const dataPrevisaoInicio = ciclo.dataPrevisaoInicio ? new Date(ciclo.dataPrevisaoInicio) : null;
          const dataPrevisaoFim = ciclo.dataPrevisaoFim ? new Date(ciclo.dataPrevisaoFim) : null;
          
          if (dataPrevisaoInicio) dataPrevisaoInicio.setHours(0, 0, 0, 0);
          if (dataPrevisaoFim) dataPrevisaoFim.setHours(0, 0, 0, 0);

          // Finalizar Ciclo (vermelho) - Período de colheita terminou
          if (dataPrevisaoFim && today > dataPrevisaoFim) {
            status = 'FINALIZAR_CICLO';
          } 
          // Em Andamento (verde) - Dentro do período de colheita
          else if (dataPrevisaoInicio && dataPrevisaoFim && today >= dataPrevisaoInicio && today <= dataPrevisaoFim) {
            status = 'EM_ANDAMENTO';
          }
          // Próximo (azul) - Ainda não começou o período
          else {
            status = 'PROXIMO';
          }
        }

        // Build ciclo code (SXX/YYYY)
        const cicloCode = `S${semanaPrevista}/${anoPrevisto}`;

        // Parse dates for sorting
        const dataPrevisaoInicioDate = ciclo.dataPrevisaoInicio ? new Date(ciclo.dataPrevisaoInicio) : null;
        const dataPrevisaoFimDate = ciclo.dataPrevisaoFim ? new Date(ciclo.dataPrevisaoFim) : null;
        if (dataPrevisaoInicioDate) dataPrevisaoInicioDate.setHours(0, 0, 0, 0);
        if (dataPrevisaoFimDate) dataPrevisaoFimDate.setHours(0, 0, 0, 0);

        data.push({
          setor,
          ciclo,
          cor,
          cicloCode,
          semanasPrevistas,
          semanasRealizadas,
          planejado,
          colhido,
          perda,
          saldo,
          percentualColhido,
          percentualPerda,
          status,
          dataPrevisaoInicio: dataPrevisaoInicioDate,
          dataPrevisaoFim: dataPrevisaoFimDate,
          anoPrevisto,
          semanaPrevista,
        });
      });
    });

    // Sort by dataPrevisaoInicio (oldest first), then by setor name
    return data.sort((a, b) => {
      const aDate = a.dataPrevisaoInicio ? a.dataPrevisaoInicio.getTime() : Infinity;
      const bDate = b.dataPrevisaoInicio ? b.dataPrevisaoInicio.getTime() : Infinity;
      
      if (aDate !== bDate) return aDate - bDate;
      return a.setor.nome.localeCompare(b.setor.nome);
    });
  }, [ciclos, setores, mangaras, colheitas, perdas, cores, variedades]);

  // Apply filters
  const filteredData = useMemo(() => {
    let result = performanceData;
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);

    // Filter by period (date ranges based on predicted harvest week year)
    if (filtroPeriodo === "ano_atual") {
      const anoAtual = hoje.getFullYear();
      result = result.filter(row => row.anoPrevisto === anoAtual);
    } else if (filtroPeriodo === "ano_anterior") {
      const anoAnterior = hoje.getFullYear() - 1;
      result = result.filter(row => row.anoPrevisto === anoAnterior);
    } else if (filtroPeriodo === "mes_atual") {
      const inicioMes = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
      const fimMes = new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0, 23, 59, 59, 999);
      
      result = result.filter(row => {
        if (row.dataPrevisaoInicio && row.dataPrevisaoFim) {
          return row.dataPrevisaoInicio <= fimMes && row.dataPrevisaoFim >= inicioMes;
        }
        return false;
      });
    } else if (filtroPeriodo === "mes_anterior") {
      const inicioMesAnterior = new Date(hoje.getFullYear(), hoje.getMonth() - 1, 1);
      const fimMesAnterior = new Date(hoje.getFullYear(), hoje.getMonth(), 0, 23, 59, 59, 999);
      
      result = result.filter(row => {
        if (row.dataPrevisaoInicio && row.dataPrevisaoFim) {
          return row.dataPrevisaoInicio <= fimMesAnterior && row.dataPrevisaoFim >= inicioMesAnterior;
        }
        return false;
      });
    } else if (filtroPeriodo === "personalizado" && dataInicio && dataFim) {
      const inicio = new Date(dataInicio);
      inicio.setHours(0, 0, 0, 0);
      const fim = new Date(dataFim);
      fim.setHours(23, 59, 59, 999);
      
      result = result.filter(row => {
        if (row.dataPrevisaoInicio && row.dataPrevisaoFim) {
          return row.dataPrevisaoInicio <= fim && row.dataPrevisaoFim >= inicio;
        }
        return false;
      });
    }
    // filtroPeriodo === "tudo" shows all records

    // Filter by setor
    if (setorFilter !== "todos") {
      result = result.filter(row => row.setor.id === setorFilter);
    }

    // Filter by cor da fita
    if (corFitaFilter !== "todas") {
      result = result.filter(row => row.cor?.id === corFitaFilter);
    }

    // Filter by status
    if (statusFilter !== "todos") {
      result = result.filter(row => row.status === statusFilter);
    }

    return result;
  }, [performanceData, setorFilter, corFitaFilter, statusFilter, filtroPeriodo, dataInicio, dataFim]);

  // Calculate totals for current view
  const totals = useMemo(() => {
    const totalPlanejado = filteredData.reduce((sum, row) => sum + row.planejado, 0);
    const totalColhido = filteredData.reduce((sum, row) => sum + row.colhido, 0);
    const totalPerda = filteredData.reduce((sum, row) => sum + row.perda, 0);
    const totalSaldo = filteredData.reduce((sum, row) => sum + row.saldo, 0);
    const percentualColhido = totalPlanejado > 0 ? (totalColhido / totalPlanejado) * 100 : 0;
    const percentualPerda = totalPlanejado > 0 ? (totalPerda / totalPlanejado) * 100 : 0;

    return {
      planejado: totalPlanejado,
      colhido: totalColhido,
      perda: totalPerda,
      saldo: totalSaldo,
      percentualColhido,
      percentualPerda,
    };
  }, [filteredData]);

  // Mesmos tons de Controle de fitas — status de ciclo é ESTADO, e é o mesmo
  // estado nas duas telas. Antes eram quatro pares de classes fixas repetidos.
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'FINALIZADO':
        return <StatusBadge tom="neutro">Finalizado</StatusBadge>;
      case 'FINALIZAR_CICLO':
        return <StatusBadge tom="erro" ponto>Finalizar ciclo</StatusBadge>;
      case 'EM_ANDAMENTO':
        return <StatusBadge tom="sucesso" ponto>Em andamento</StatusBadge>;
      case 'PROXIMO':
        return <StatusBadge tom="info" ponto>Próximo</StatusBadge>;
      default:
        return null;
    }
  };

  const handleExportExcel = () => {
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const dataToExport: any[] = filteredData.map(row => ({
        "Setor": row.setor.nome,
        "Cor/Fita": row.cor?.nome || "-",
        "Ciclo": row.cicloCode,
        "Prev (S)": row.semanasPrevistas,
        "Real (S)": row.semanasRealizadas ?? "-",
        "Planejado": row.planejado,
        "Colhido": row.colhido,
        "Perda": row.perda,
        "Saldo": row.saldo,
        "% Colhido": row.percentualColhido.toFixed(1),
        "% Perda": row.percentualPerda.toFixed(1),
        "Status": row.status === 'FINALIZADO' ? 'Finalizado' : 
                 row.status === 'FINALIZAR_CICLO' ? 'Finalizar Ciclo' : 
                 row.status === 'EM_ANDAMENTO' ? 'Em Andamento' : 'Próximo',
      }));

      // Add totals row
      dataToExport.push({
        "Setor": "TOTAL",
        "Cor/Fita": "",
        "Ciclo": "",
        "Prev (S)": "",
        "Real (S)": "",
        "Planejado": totals.planejado,
        "Colhido": totals.colhido,
        "Perda": totals.perda,
        "Saldo": totals.saldo,
        "% Colhido": totals.percentualColhido.toFixed(1),
        "% Perda": totals.percentualPerda.toFixed(1),
        "Status": "TOTAL",
      });

      const ws = XLSX.utils.json_to_sheet(dataToExport);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Performance");

      // Auto-adjust column widths
      const colWidths = [15, 15, 12, 10, 10, 12, 12, 10, 10, 12, 12, 18];
      ws["!cols"] = colWidths.map(w => ({ wch: w }));

      const filename = `performance_setor_${new Date().toISOString().split("T")[0]}.xlsx`;
      XLSX.writeFile(wb, filename);

      toast({
        title: "Exportado com sucesso",
        description: `Arquivo ${filename} foi baixado`,
      });
    } catch (error) {
      console.error("Erro ao exportar:", error);
      toast({
        title: "Erro ao exportar",
        description: "Não foi possível gerar o arquivo",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="space-y-4 md:space-y-6">
      <div className="flex items-start gap-3">
        <Link href="/cacho/relatorios">
          <Button variant="ghost" size="icon" data-testid="button-voltar-relatorios">
            <ArrowLeft className="h-5 w-5" />
          </Button>
        </Link>
        <div className="flex-1 min-w-0">
          <PageHeader
            titulo="Performance por setor"
            descricao="Análise detalhada por setor e ciclo"
            acao={
              <Button variant="outline" className={ALVO_TOQUE_BOTAO} onClick={handleExportExcel} data-testid="button-export-excel">
                <Download className="h-4 w-4 mr-2" />
                Exportar
              </Button>
            }
          />
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader className="p-4 md:p-6 pb-3">
          <CardTitle className="text-base md:text-lg flex items-center gap-2">
            <Filter className="h-4 w-4 md:h-5 md:w-5" />
            Filtros
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 md:p-6 pt-0">
          <div className="grid gap-4 md:gap-6 grid-cols-1 md:grid-cols-5">
            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={statusFilter} onValueChange={v => definir({ status: v })}>
                <SelectTrigger data-testid="select-status">
                  <SelectValue placeholder="Todos os Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos os Status</SelectItem>
                  <SelectItem value="EM_ANDAMENTO">Em Andamento</SelectItem>
                  <SelectItem value="PROXIMO">Próximo</SelectItem>
                  <SelectItem value="FINALIZAR_CICLO">Finalizar Ciclo</SelectItem>
                  <SelectItem value="FINALIZADO">Finalizado</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Setor</Label>
              <Select value={setorFilter} onValueChange={v => definir({ setor: v })}>
                <SelectTrigger data-testid="select-setor">
                  <SelectValue placeholder="Todos os Setores" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos os Setores</SelectItem>
                  {setores?.filter(s => s.ativo).sort((a, b) => a.nome.localeCompare(b.nome)).map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.nome}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Cor da Fita</Label>
              <Select value={corFitaFilter} onValueChange={v => definir({ cor: v })}>
                <SelectTrigger data-testid="select-cor-fita">
                  <SelectValue placeholder="Todas as Cores" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todas">Todas as Cores</SelectItem>
                  {cores?.sort((a, b) => a.nome.localeCompare(b.nome)).map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full border" style={{ backgroundColor: c.hexColor }} />
                        {c.nome}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Período</Label>
              <Select value={filtroPeriodo} onValueChange={v => definir({ periodo: v })}>
                <SelectTrigger data-testid="select-periodo">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="tudo">Tudo</SelectItem>
                  <SelectItem value="ano_atual">Ano Atual</SelectItem>
                  <SelectItem value="ano_anterior">Ano Anterior</SelectItem>
                  <SelectItem value="mes_atual">Mês Atual</SelectItem>
                  <SelectItem value="mes_anterior">Mês Anterior</SelectItem>
                  <SelectItem value="personalizado">Período Específico</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-end">
              <span className="text-sm text-muted-foreground whitespace-nowrap pb-2">
                {filteredData.length} registros encontrados
              </span>
            </div>

            {filtroPeriodo === "personalizado" && (
              <>
                <div className="space-y-2">
                  <Label>Data Início</Label>
                  <Input
                    type="date"
                    value={dataInicio}
                    onChange={(e) => definir({ de: e.target.value })}
                    data-testid="input-data-inicio"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Data Fim</Label>
                  <Input
                    type="date"
                    value={dataFim}
                    onChange={(e) => definir({ ate: e.target.value })}
                    data-testid="input-data-fim"
                  />
                </div>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Um DataCard só para o conjunto — é aqui que o gráfico entra quando
          existir; hoje esta tela ainda não tem nenhum. */}
      <DataCard titulo="Desempenho por setor e ciclo" meta={`${filteredData.length} registro(s)`}>
          {isLoading ? (
            <PageSkeleton variante="tabela" linhas={5} className="border-0 rounded-none" />
          ) : filteredData.length === 0 ? (
            <EmptyState
              titulo="Nenhum dado no recorte"
              descricao="Amplie o período ou limpe os filtros acima — a performance sai das fitas com colheita lançada."
              testId="empty-performance"
            />
          ) : (
            <>
              {/* Mobile: Cards View */}
              <div className="space-y-2 p-4 md:hidden">
                {filteredData.map((row, idx) => (
                  <Card key={`${row.setor.id}-${row.ciclo.id}`} className="border" data-testid={`performance-card-${idx}`}>
                    <CardContent className="p-3 space-y-2">
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-sm">{row.setor.nome}</span>
                          <div 
                            className="w-3 h-3 rounded-full border"
                            style={{ backgroundColor: row.cor?.hexColor || '#ccc' }}
                          />
                          <span className="text-xs text-muted-foreground">{row.cor?.nome}</span>
                        </div>
                        {getStatusBadge(row.status)}
                      </div>
                      <div className="text-xs text-muted-foreground font-mono">
                        Ciclo: {row.cicloCode}
                      </div>
                      <div className="grid grid-cols-3 gap-2 text-xs">
                        <div>
                          <span className="text-muted-foreground">Prev (S):</span>
                          <div className="font-mono font-medium">{row.semanasPrevistas}</div>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Real (S):</span>
                          <div className="font-mono font-medium">{row.semanasRealizadas ?? '-'}</div>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Planejado:</span>
                          <div className="font-mono font-medium">{row.planejado.toLocaleString('pt-BR')}</div>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Colhido:</span>
                          <div className="font-mono font-medium">{row.colhido.toLocaleString('pt-BR')}</div>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Perda:</span>
                          <div className="font-mono font-medium text-erro">{row.perda}</div>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Saldo:</span>
                          <div className="font-mono font-medium">{row.saldo}</div>
                        </div>
                        <div>
                          <span className="text-muted-foreground">% Colhido:</span>
                          <div className="font-mono font-medium">{row.percentualColhido.toFixed(1)}%</div>
                        </div>
                        <div>
                          <span className="text-muted-foreground">% Perda:</span>
                          <div className="font-mono font-medium text-erro">{row.percentualPerda.toFixed(1)}%</div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
                {/* Mobile Totals Card */}
                {filteredData.length > 0 && (
                  <Card className="border-t-2 border-primary bg-muted/50" data-testid="totals-card-mobile">
                    <CardContent className="p-3 space-y-2">
                      <div className="text-xs font-bold text-muted-foreground">
                        TOTAIS (VISUALIZAÇÃO ATUAL)
                      </div>
                      <div className="grid grid-cols-3 gap-2 text-xs">
                        <div>
                          <span className="text-muted-foreground">Planejado:</span>
                          <div className="font-mono font-bold">{totals.planejado.toLocaleString('pt-BR')}</div>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Colhido:</span>
                          <div className="font-mono font-bold">{totals.colhido.toLocaleString('pt-BR')}</div>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Perda:</span>
                          <div className="font-mono font-bold text-erro">{totals.perda.toLocaleString('pt-BR')}</div>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Saldo:</span>
                          <div className="font-mono font-bold">{totals.saldo.toLocaleString('pt-BR')}</div>
                        </div>
                        <div>
                          <span className="text-muted-foreground">% Colhido:</span>
                          <div className="font-mono font-bold">{totals.percentualColhido.toFixed(1)}%</div>
                        </div>
                        <div>
                          <span className="text-muted-foreground">% Perda:</span>
                          <div className="font-mono font-bold text-erro">{totals.percentualPerda.toFixed(1)}%</div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>

              {/* Desktop: Table View */}
              <div className="hidden md:block overflow-x-auto">
                <DataTable colunas={COLUNAS}>
                  <DataTableHeader>
                    <DataTableRow>
                      <DataTableHead>Setor</DataTableHead>
                      <DataTableHead>Cor / Fita</DataTableHead>
                      <DataTableHead>Ciclo</DataTableHead>
                      <DataTableHead className="text-right">Previsto (S)</DataTableHead>
                      <DataTableHead className="text-right">Realizado (S)</DataTableHead>
                      <DataTableHead className="text-right">Planejado</DataTableHead>
                      <DataTableHead className="text-right">Colhido</DataTableHead>
                      <DataTableHead className="text-right">Perda</DataTableHead>
                      <DataTableHead className="text-right">Saldo</DataTableHead>
                      <DataTableHead className="text-right">% Colhido</DataTableHead>
                      <DataTableHead className="text-right">% Perda</DataTableHead>
                      <DataTableHead>Status</DataTableHead>
                      <DataTableHead className="w-10"></DataTableHead>
                    </DataTableRow>
                  </DataTableHeader>
                  <DataTableBody>
                    {filteredData.map((row, idx) => (
                      <DataTableRow key={`${row.setor.id}-${row.ciclo.id}`} data-testid={`performance-row-${idx}`}>
                        <DataTableCell className="font-medium">{row.setor.nome}</DataTableCell>
                        <DataTableCell>
                          <div className="flex items-center gap-2">
                            <div 
                              className="w-3 h-3 rounded-full border"
                              style={{ backgroundColor: row.cor?.hexColor || '#ccc' }}
                            />
                            <span className="text-sm">{row.cor?.nome}</span>
                          </div>
                        </DataTableCell>
                        <DataTableCell className="font-mono text-sm text-muted-foreground">{row.cicloCode}</DataTableCell>
                        <DataTableNum>{row.semanasPrevistas}</DataTableNum>
                        <DataTableNum>{row.semanasRealizadas ?? '-'}</DataTableNum>
                        <DataTableNum className="font-semibold">{row.planejado.toLocaleString('pt-BR')}</DataTableNum>
                        <DataTableNum>{row.colhido.toLocaleString('pt-BR')}</DataTableNum>
                        <DataTableNum className="text-erro">{row.perda}</DataTableNum>
                        <DataTableNum>{row.saldo}</DataTableNum>
                        <DataTableNum>{row.percentualColhido.toFixed(1)}%</DataTableNum>
                        <DataTableNum className="text-erro">{row.percentualPerda.toFixed(1)}%</DataTableNum>
                        <DataTableCell>{getStatusBadge(row.status)}</DataTableCell>
                        <DataTableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`menu-row-${idx}`}>
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem asChild>
                                <Link href={`/cacho/ciclos/${row.ciclo.id}`} state={origemAtual()}>Ver Ciclo</Link>
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </DataTableCell>
                      </DataTableRow>
                    ))}
                    {/* Totals Row */}
                    {filteredData.length > 0 && (
                      <DataTableRow className="bg-muted/50 hover:bg-muted/50 border-t-2 font-semibold" data-testid="totals-row">
                        <DataTableCell colSpan={5} className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                          Totais (visualização atual)
                        </DataTableCell>
                        <DataTableNum className="font-bold">{totals.planejado.toLocaleString('pt-BR')}</DataTableNum>
                        <DataTableNum className="font-bold">{totals.colhido.toLocaleString('pt-BR')}</DataTableNum>
                        <DataTableNum className="font-bold text-erro">{totals.perda.toLocaleString('pt-BR')}</DataTableNum>
                        <DataTableNum className="font-bold">{totals.saldo.toLocaleString('pt-BR')}</DataTableNum>
                        <DataTableNum className="font-bold">{totals.percentualColhido.toFixed(1)}%</DataTableNum>
                        <DataTableNum className="font-bold text-erro">{totals.percentualPerda.toFixed(1)}%</DataTableNum>
                        <DataTableCell></DataTableCell>
                        <DataTableCell></DataTableCell>
                      </DataTableRow>
                    )}
                  </DataTableBody>
                </DataTable>
              </div>
            </>
          )}
      </DataCard>

    </div>
  );
}
